import {defineMessages, FormattedMessage} from 'react-intl';
import {Button, Icon, view} from '@sail/ui';
import {useCallback} from 'react';
import {cancelCircle} from '@sail/icons/react/Icon';
import {TopBar} from '../TopBar';
import {SpeakerButton} from './SpeakerButton';
import {ScreenshareButton} from '../ScreenshareButton';
import {useWidgetControls} from '../../useWidget';
import {useEnabledScreenshareUi} from '../../../FeatureFlagsProvider';

const messages = defineMessages({
  chattingWithName: {
    id: 'conversations_ui.widget.liveChatTopBar.chattingWithName',
    description:
      'Text indicating name of support agent the user is chatting with',
    defaultMessage: 'Chatting with {name}',
  },
  connectingWithAgent: {
    id: 'conversations_ui.widget.liveChatTopBar.chattingWithGeneric',
    description: 'Text indicating who the user is chatting stripe support',
    defaultMessage: 'Connecting with agent',
  },
  endChatButton: {
    id: 'conversations_ui.widget.liveChatTopBar.endChatButton',
    description: 'Button to end conversation with an agent',
    defaultMessage: 'End chat',
  },
});

type Props = {
  agentNames: string[];
};

const Title = ({agentNames}: {agentNames: string[]}) => (
  <view.div css={{font: 'label.small.emphasized'}}>
    {agentNames.length > 0 ? (
      <FormattedMessage
        {...messages.chattingWithName}
        values={{name: agentNames.join(', ')}}
      />
    ) : (
      <FormattedMessage {...messages.connectingWithAgent} />
    )}
  </view.div>
);

const Controls = ({onClose}: {onClose: () => void}) => (
  <view.div css={{stack: 'x', alignX: 'end'}}>
    <SpeakerButton />
    <Button
      size="small"
      onPress={onClose}
      data-testid="conversations-widget-end-chat-button"
    >
      <FormattedMessage {...messages.endChatButton} />
    </Button>
  </view.div>
);

const ControlsScreenshare = ({
  onClose,
  agentJoined,
}: {
  onClose: () => void;
  agentJoined: boolean;
}) => (
  <view.div
    css={{
      stack: 'x',
      gap: 'small',
      width: 'fill',
    }}
  >
    <ScreenshareButton agentJoined={agentJoined} />
    <Button
      size="small"
      onPress={onClose}
      data-testid="conversations-widget-end-chat-button"
      css={{flex: 1, gap: 'small', height: '28px'}}
    >
      <Icon icon={cancelCircle} />
      <FormattedMessage {...messages.endChatButton} />
    </Button>
    <SpeakerButton screenshareRender />
  </view.div>
);

export const LiveChatTopBar = ({agentNames}: Props) => {
  const {close} = useWidgetControls();
  const isScreenshareEnabled = useEnabledScreenshareUi();

  const onClose = useCallback(() => {
    close(false);
  }, [close]);

  const agentJoined = agentNames.length > 0;

  return (
    <TopBar data-testid="widget-topbar-live-chat">
      {isScreenshareEnabled ? (
        <view.div css={{stack: 'y', gap: 'small'}}>
          <Title agentNames={agentNames} />
          <view.div css={{stack: 'x'}}>
            <ControlsScreenshare onClose={onClose} agentJoined={agentJoined} />
          </view.div>
        </view.div>
      ) : (
        <view.div css={{stack: 'x'}}>
          <Title agentNames={agentNames} />
          <Controls onClose={onClose} />
        </view.div>
      )}
    </TopBar>
  );
};
